import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";

const HTML_OPEN_TAG = /<[a-zA-Z][\w-]*(?:\s[^>]*)?>/;
const HTML_CLOSE_TAG = /<\/\s*[a-zA-Z][\w-]*\s*>/;
const INJECTION_PATTERNS = [HTML_OPEN_TAG, HTML_CLOSE_TAG];

const MIN_DATE = new Date(2020, 0, 1);
const MAX_DATE = new Date(2050, 11, 31);

const MAX_IMAGE_SIZE = 50 * 1024 * 1024; // 50MB

export const noInjection = (value) => !INJECTION_PATTERNS.some((p) => p.test(value));

const toOptionalNumber = (_, originalValue) => {
  if (originalValue === "" || originalValue == null || originalValue === "none") return undefined;
  const n = Number(originalValue);
  return Number.isNaN(n) ? NaN : n;
};

export const createFieldPresets = (t) => {
  const maxYear = new Date().getFullYear();

  return {
    nonStrictString: Yup.string().test("no-injection", t("validation.plainText"), noInjection),

    email: Yup.string()
      .required(t("validation.required"))
      .email(t("validation.email"))
      .test("after-dot", t("validation.email"), (value) => {
        if (!value) return true;
        const parts = value.split(".");

        return parts[1]?.length >= 2;
      }),

    textShort: Yup.string()
      .transform((value) => value.trim())
      .required(t("validation.required"))
      .min(2, t("validation.minLength", { minLength: 2 }))
      .max(100, t("validation.maxLength", { maxLength: 100 }))
      .test("no-injection", t("validation.plainText"), noInjection),

    textLong: Yup.string()
      .transform((value) => value.trim())
      .max(5000, t("validation.maxLength", { maxLength: 5000 }))
      .test("no-injection", t("validation.plainText"), noInjection),

    details: Yup.string()
      .transform((value) => value.trim())
      .max(5000, t("validation.maxLength", { maxLength: 5000 })),

    phone: Yup.string()
      .required(t("validation.required"))
      .test("phone-format", t("validation.invalidPhoneNumber"), (v) => isValidPhoneNumber(v)),

    calendarDate: Yup.string()
      .required(t("validation.required"))
      .test("valid-date-range", t("validation.invalidDate"), (v) => {
        const d = new Date(v);
        return !Number.isNaN(d.getTime()) && d >= MIN_DATE && d <= MAX_DATE;
      }),

    year: Yup.number()
      .test("all-digits", t("validation.invalidYear"), (v) =>
        typeof v === "string" ? !/\D/.test(v) : true,
      )
      .transform(toOptionalNumber)
      .integer(t("validation.invalidYear"))
      .typeError(t("validation.invalidYear"))
      .min(1000, t("validation.yearTooEarly", { minYear: 1000 }))
      .max(maxYear, t("validation.yearTooLate", { maxYear })),

    optionalNumber: Yup.number()
      .transform(toOptionalNumber)
      .typeError(t("validation.invalidNumber")),

    latitude: Yup.number().test(
      "valid-latitude",
      t("validation.invalidLatitude"),
      (v) => v >= -90 && v <= 90,
    ),

    longitude: Yup.number().test(
      "valid-longitude",
      t("validation.invalidLongitude"),
      (v) => v >= -180 && v <= 180,
    ),

    postalCode: Yup.string()
      .required(t("validation.required"))
      .test("numbers-only", t("validation.invalidPostalCode"), (v) => !/\D/.test(v))
      .min(3, t("validation.minLength", { minLength: 3 }))
      .max(11, t("validation.maxLength", { maxLength: 11 })),

    image: Yup.mixed().test(
      "file-size",
      t("validation.fileSizeExceeded", { maxSize: MAX_IMAGE_SIZE / 1024 / 1024 }),
      (value) => {
        if (!value || typeof value === "string") return true;
        return value.size <= MAX_IMAGE_SIZE;
      },
    ),
  };
};
